import { readableColor } from 'polished';
import { colors } from '@ardoq/design-tokens';
import type { ArdoqIcon } from '@ardoq/data-model';
import {
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_ICON_SIZE as ICON_SIZE,
  MODERNIZED_BLOCK_DIAGRAM_IMAGE_CIRCLE_STROKE_WIDTH as CIRCLE_STROKE_WIDTH,
} from 'tabview/blockDiagram/view/yFilesExtensions/modernized/consts';
import { paintIcon } from 'tabview/canvasRendering/paintIcon';
import { paintImageInCircle } from 'tabview/canvasRendering/paintImageInCircle';

const IMAGE_MARGIN = 8;

/**
 * The component-type "representation" at the top of a leaf node card, shared by
 * the Modernized Block Diagram's `LeafNodeVisual` and the Assistant Diagram's
 * component card. Both forms are centered on `(cx, cy)` and sized from the circle
 * `radius`; the caller manages save/restore of broader context state.
 */

/** An icon centered in a filled, type-colored circle. */
export const paintIconRepresentation = (
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  color: string,
  icon: ArdoqIcon
): void => {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.ellipse(cx, cy, radius, radius, 0, 0, 2 * Math.PI);
  ctx.fill();
  const iconColor = readableColor(color, colors.white950, colors.grey950);
  paintIcon(ctx, cx, cy, ICON_SIZE, iconColor, icon);
};

/** An image clipped into a circle with a type-colored outline. */
export const paintImageRepresentation = (
  ctx: CanvasRenderingContext2D,
  imageHref: string,
  cx: number,
  cy: number,
  radius: number,
  color: string
): void => {
  const imageRadius = radius - CIRCLE_STROKE_WIDTH;
  const outlineRadius = radius - CIRCLE_STROKE_WIDTH / 2;
  const imageSize = 2 * radius - 2 * IMAGE_MARGIN;
  paintImageInCircle(ctx, imageHref, cx, cy, imageRadius, imageSize);
  const { lineWidth } = ctx;
  ctx.lineWidth = CIRCLE_STROKE_WIDTH;
  ctx.strokeStyle = color;
  ctx.beginPath();
  ctx.ellipse(cx, cy, outlineRadius, outlineRadius, 0, 0, 2 * Math.PI);
  ctx.stroke();
  ctx.lineWidth = lineWidth;
};
